import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { getEconomyState, invalidateEconomyState } from '../../utils/redisCache';

const EconomyDashboardPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [economyState, setEconomyState] = useState(null);
  const [tmsHistory, setTmsHistory] = useState([]);
  const [paramHistory, setParamHistory] = useState([]);
  const [stressTests, setStressTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextTickIn, setNextTickIn] = useState(60);
  const [runningTest, setRunningTest] = useState(null);
  const [testMsg, setTestMsg] = useState(null);
  const [econFromCache, setEconFromCache] = useState(false);

  const fetchEconomyData = useCallback(async () => {
    try {
      const { data: econData, fromCache, error: econErr } = await getEconomyState();
      if (econErr) throw new Error(econErr);
      setEconomyState(econData);
      setEconFromCache(!!fromCache);

      if (econData?.next_tick_at) {
        const diff = Math.max(0, Math.floor((new Date(econData.next_tick_at) - new Date()) / 1000));
        setNextTickIn(diff);
      }

      const { data: tickLogs } = await supabase?.from('tick_execution_log')?.select('tick_number, executed_at, tick_duration_ms, tick_status')?.order('executed_at', { ascending: false })?.limit(30);
      if (tickLogs?.length > 0) {
        setTmsHistory(tickLogs?.reverse()?.map((t) => ({ tick: 'T' + t?.tick_number, duration: t?.tick_duration_ms || 0, status: t?.tick_status })));
      }

      const { data: paramData } = await supabase?.from('economic_parameter_history')?.select('parameter_name, old_value, new_value, change_percentage, created_at')?.order('created_at', { ascending: false })?.limit(10);
      setParamHistory(paramData || []);

      const { data: stressData } = await supabase?.from('stress_test_runs')?.select('*')?.order('created_at', { ascending: false })?.limit(5);
      setStressTests(stressData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load economy data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEconomyData(); }, [fetchEconomyData]);

  useRealtimeSubscription(
    'economy-state-live',
    { table: 'economy_state', event: '*' },
    useCallback(() => { invalidateEconomyState(); fetchEconomyData(); }, [fetchEconomyData]),
    { enabled: true }
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setNextTickIn(prev => {
        if (prev <= 1) { invalidateEconomyState(); fetchEconomyData(); return 60; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [fetchEconomyData]);

  const handleRunStressTest = async (testType) => {
    setRunningTest(testType);
    setTestMsg(null);
    try {
      let rpcName;
      let params = {};
      if (testType === 'tick_performance') rpcName = 'run_stress_test_tick_performance';
      else if (testType === 'inflation_spike') { rpcName = 'run_stress_test_inflation_spike'; params = { p_injection_pct: 30 }; }
      else if (testType === 'margin_cascade') rpcName = 'run_stress_test_margin_cascade';
      const { data, error } = await supabase?.rpc(rpcName, params);
      if (error) throw error;
      setTestMsg({ type: 'success', text: 'Test completed. Run ID: ' + data?.run_id });
      invalidateEconomyState();
      fetchEconomyData();
    } catch (err) {
      setTestMsg({ type: 'error', text: err?.message });
    } finally {
      setRunningTest(null);
    }
  };

  const inflationIndex = Number(economyState?.inflation_index || 0);
  const inflationStatus = inflationIndex > 25 ? 'critical' : inflationIndex > 15 ? 'warning' : 'normal';
  const statusColors = { normal: 'text-success', warning: 'text-warning', critical: 'text-red-400' };
  const statusBg = { normal: 'bg-success/10 border-success/20', warning: 'bg-warning/10 border-warning/20', critical: 'bg-red-500/10 border-red-500/20' };
  const tms = Number(economyState?.total_money_supply || 0);
  const tms7d = Number(economyState?.tms_7days_ago || 0);

  if (loading) {
    return (
      <div className="flex h-screen bg-background text-foreground overflow-hidden">
        <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} userRole="admin" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-3" />
            <p className="text-muted-foreground">Loading economy data...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(p => !p)} userRole="admin" />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-h2 font-heading text-foreground flex items-center gap-3">
                <Icon name="Activity" size={28} color="var(--color-primary)" />
                Economy Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">Global economic governance layer — live data</p>
            </div>
            <div className="text-right flex items-center gap-3">
              {econFromCache && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 border border-primary/20">
                  <Icon name="Zap" size={11} className="text-primary" />
                  <span className="text-xs text-primary font-medium">Cached</span>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground">Tick #{economyState?.tick_number || 0}</p>
                <p className="text-sm font-mono text-primary">Next tick in {nextTickIn}s</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="card p-3 mb-4 border-red-500/30">
              <p className="text-red-400 text-sm flex items-center gap-2"><Icon name="AlertCircle" size={14} />{error}</p>
            </div>
          )}

          <div className={`p-3 mb-6 rounded-lg border flex items-center gap-3 ${statusBg?.[inflationStatus]}`}>
            <Icon name={inflationStatus === 'normal' ? 'CheckCircle' : 'AlertTriangle'} size={16} className={statusColors?.[inflationStatus]} />
            <p className={`text-sm font-medium ${statusColors?.[inflationStatus]}`}>
              Inflation Index: {inflationIndex?.toFixed(2)}% —
              {inflationStatus === 'normal' && ' Economy is stable'}
              {inflationStatus === 'warning' && ' Trading fees increased, crime rewards reduced, operating costs up'}
              {inflationStatus === 'critical' && ' CRITICAL: Margin disabled, leverage capped, IPO frozen, recession active'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Money Supply', value: '$' + (tms / 1000000)?.toFixed(2) + 'M', icon: 'DollarSign', color: 'text-primary' },
              { label: 'Inflation Index', value: inflationIndex?.toFixed(2) + '%', icon: 'TrendingUp', color: statusColors?.[inflationStatus] },
              { label: 'Economy Modifier', value: '\u00d7' + Number(economyState?.economy_modifier || 1)?.toFixed(3), icon: 'Sliders', color: 'text-foreground' },
              { label: 'Leverage Cap', value: Number(economyState?.leverage_cap || 10) + '\u00d7', icon: 'Layers', color: economyState?.margin_disabled ? 'text-red-400' : 'text-foreground' },
            ]?.map((metric) => (
              <div key={metric?.label} className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">{metric?.label}</span>
                  <Icon name={metric?.icon} size={14} className={metric?.color} />
                </div>
                <p className={`text-2xl font-mono font-bold ${metric?.color}`}>{metric?.value}</p>
              </div>
            ))}
          </div>

          {tms7d > 0 && (
            <div className="card p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">TMS vs 7 Days Ago</p>
                  <p className="text-sm font-mono">
                    <span className="text-muted-foreground">7d ago: </span>
                    <span className="text-foreground">${(tms7d / 1000000)?.toFixed(2)}M</span>
                    <span className="mx-2 text-muted-foreground">&#8594;</span>
                    <span className="text-primary">${(tms / 1000000)?.toFixed(2)}M</span>
                    <span className={`ml-2 text-sm font-bold ${inflationIndex > 0 ? 'text-warning' : 'text-success'}`}>({inflationIndex > 0 ? '+' : ''}{inflationIndex?.toFixed(2)}%)</span>
                  </p>
                </div>
                <Icon name="TrendingUp" size={20} className={inflationIndex > 15 ? 'text-warning' : 'text-success'} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Recession State', active: economyState?.recession_state, icon: 'TrendingDown' },
              { label: 'Margin Disabled', active: economyState?.margin_disabled, icon: 'XCircle' },
              { label: 'IPO Frozen', active: economyState?.ipo_frozen, icon: 'Pause' },
            ]?.map((flag) => (
              <div key={flag?.label} className={`card p-3 flex items-center gap-3 ${flag?.active ? 'border-red-500/30' : 'border-border'}`}>
                <Icon name={flag?.icon} size={16} color={flag?.active ? '#EF4444' : 'var(--color-muted-foreground)'} />
                <div>
                  <p className="text-xs font-semibold text-foreground">{flag?.label}</p>
                  <p className={`text-xs ${flag?.active ? 'text-red-400' : 'text-success'}`}>{flag?.active ? 'ACTIVE' : 'Normal'}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Trading Fee Multiplier', value: Number(economyState?.trading_fee_multiplier || 1)?.toFixed(2) + '\u00d7', warn: Number(economyState?.trading_fee_multiplier || 1) > 1.5 },
              { label: 'Crime Reward Multiplier', value: Number(economyState?.crime_reward_multiplier || 1)?.toFixed(2) + '\u00d7', warn: Number(economyState?.crime_reward_multiplier || 1) < 0.75 },
              { label: 'Operating Cost Multiplier', value: Number(economyState?.operating_cost_multiplier || 1)?.toFixed(2) + '\u00d7', warn: Number(economyState?.operating_cost_multiplier || 1) > 1.5 },
            ]?.map((m) => (
              <div key={m?.label} className="card p-3">
                <p className="text-xs text-muted-foreground mb-1">{m?.label}</p>
                <p className={`text-lg font-mono font-bold ${m?.warn ? 'text-warning' : 'text-foreground'}`}>{m?.value}</p>
              </div>
            ))}
          </div>

          {tmsHistory?.length > 0 && (
            <div className="card p-5 mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Tick Performance History</h3>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={tmsHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="tick" tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }} tickLine={false} interval={4} />
                  <YAxis tick={{ fontSize: 9, fill: 'var(--color-muted-foreground)' }} tickLine={false} width={40} />
                  <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }} labelStyle={{ color: 'var(--color-muted-foreground)', fontSize: 11 }} itemStyle={{ color: 'var(--color-primary)', fontSize: 12 }} />
                  <Area type="monotone" dataKey="duration" stroke="var(--color-primary)" fill="var(--color-primary)" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {paramHistory?.length > 0 && (
            <div className="card p-5 mb-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Recent Parameter Changes</h3>
              <div className="space-y-2">
                {paramHistory?.map((p, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border/30 text-sm">
                    <span className="text-foreground font-medium">{p?.parameter_name?.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-muted-foreground">{Number(p?.old_value)?.toFixed(3)}</span>
                      <Icon name="ArrowRight" size={12} className="text-muted-foreground" />
                      <span className="font-mono text-primary">{Number(p?.new_value)?.toFixed(3)}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${Number(p?.change_percentage) > 0 ? 'text-warning bg-warning/10' : 'text-success bg-success/10'}`}>{Number(p?.change_percentage) > 0 ? '+' : ''}{Number(p?.change_percentage)?.toFixed(1)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card p-5">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Stress Tests</h3>
            {testMsg && (
              <div className={`p-3 rounded-lg mb-4 text-sm ${testMsg?.type === 'success' ? 'bg-success/10 text-success border border-success/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>{testMsg?.text}</div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[
                { type: 'tick_performance', label: 'Tick Performance', icon: 'Activity', color: 'text-primary' },
                { type: 'inflation_spike', label: 'Inflation Spike +30%', icon: 'TrendingUp', color: 'text-warning' },
                { type: 'margin_cascade', label: 'Margin Cascade', icon: 'TrendingDown', color: 'text-red-400' },
              ]?.map((test) => (
                <button key={test?.type} onClick={() => handleRunStressTest(test?.type)} disabled={!!runningTest} className="card p-4 text-left hover:border-primary/40 transition-all disabled:opacity-50">
                  <Icon name={runningTest === test?.type ? 'Loader' : test?.icon} size={18} className={`${test?.color} mb-2 ${runningTest === test?.type ? 'animate-spin' : ''}`} />
                  <p className="text-sm font-medium text-foreground">{test?.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{runningTest === test?.type ? 'Running...' : 'Click to run'}</p>
                </button>
              ))}
            </div>
            {stressTests?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs text-muted-foreground uppercase mb-2">Recent Runs</p>
                <div className="space-y-1">
                  {stressTests?.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b border-border/30">
                      <span className="text-foreground">{t?.test_type?.replace(/_/g, ' ')}</span>
                      <span className={`px-1.5 py-0.5 rounded ${t?.status === 'completed' ? 'bg-success/10 text-success' : 'bg-red-500/10 text-red-400'}`}>{t?.status}</span>
                      <span className="text-muted-foreground">{new Date(t?.created_at)?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default EconomyDashboardPage;
